import { useTheme } from '@mui/material'
import React, { useState } from 'react'
import { Area, AreaChart, CartesianGrid, Label, ReferenceLine, ResponsiveContainer, Tooltip, TooltipProps } from 'recharts'
import { ValueType } from 'recharts/types/component/DefaultTooltipContent'
import { makeStyles } from 'tss-react/mui'
import usePowerPriceChartConfig from '../../../../dynamic-power-prices/hooks/use-power-price-chart-config'
import useReferenceLineConfig from '../../../../dynamic-power-prices/hooks/use-reference-line-config'
import { AreaDot, DynamicEmsDataModel, DynamicPriceType, EmsBatteryMode, PowerPrice } from '../../../../dynamic-power-prices/types'
import HoverDot from './HoverDot'
import moment from 'moment'
import CustomizedDot from './CustomizedDot'
import { useIsDynamicEmsAccessible } from 'src/Libs/dynamic-power-prices'
import XAxisPowerPriceChart from './XAxisPowerPriceChart'
import YAxisPowerPriceChart from './YAxisPowerPriceChart'
import { isAfter } from 'date-fns'
import PowerPriceChartAI360 from './PowerPriceChartAI360'

interface Props {
  to: string /* date string until which power prices should be displayed */
  wallboxLimit: number
  batteryLimit: number
  powerPrices: PowerPrice[]
  currentPowerPriceValues: PowerPrice
  isModal?: boolean
  selectedDynamicPowerPriceModalSetting?: DynamicPriceType
  isDynamicEmsEnabled: boolean | undefined
  getTooltipValues?: (props: TooltipProps<ValueType, keyof PowerPrice>) => JSX.Element
  dynamicEmsData?: DynamicEmsDataModel[]
  isDynamicEmsDataLoading?: boolean
  minChartHeight?: string
  isDynamicPowerPriceEnabledForBattery: boolean
  isDynamicPowerPriceEnabledForWallboxes: boolean
}

const useStyles = makeStyles()((theme, props) => ({
  chartContainer: {
    width: '100%',
    fontSize: 11,

    '> .recharts-wrapper': {
      '> .recharts-surface': {
        borderRadius: theme.shape.borderRadius,
      },
    },

    [theme.breakpoints.down('sm')]: {
      fontSize: 14,
    },
  },
}))

/**
 * Component to create the power price chart
 */
const PowerPriceChart: React.FC<Props> = ({
  to,
  wallboxLimit,
  batteryLimit,
  powerPrices,
  currentPowerPriceValues,
  isModal,
  isDynamicEmsEnabled,
  getTooltipValues,
  selectedDynamicPowerPriceModalSetting = DynamicPriceType.Wallbox,
  dynamicEmsData,
  isDynamicEmsDataLoading,
  minChartHeight,
  isDynamicPowerPriceEnabledForBattery,
  isDynamicPowerPriceEnabledForWallboxes,
}) => {
  const theme = useTheme()
  const { classes } = useStyles()
  const [showStaticDot, setShowStaticDot] = useState<boolean>(true)
  const lastPowerPriceIndex = powerPrices.length - 1
  const powerPriceEndTo = powerPrices[lastPowerPriceIndex]?.validTo
  const displayTo = isAfter(new Date(to), new Date(powerPriceEndTo)) ? powerPriceEndTo : to
  const { isDynamicEmsV2Accessible } = useIsDynamicEmsAccessible()

  const { chartData, chartColorConfig, hasNegativePrices, yAxisDomain, minPrice, maxPrice } = usePowerPriceChartConfig({
    to: displayTo,
    powerPrices,
    currentPowerPriceValues,
    wallboxLimit,
    batteryLimit,
    selectedDynamicPowerPriceModalSetting,
    isModal,
    isDynamicEmsEnabled,
  })

  const {
    dynamicPriceColorConfig,
    showReferenceLineForWallbox,
    showReferenceLineForBattery,
    wallboxReferenceLineYPosition,
    batteryReferenceLineYPosition,
    formattedWallboxLimit,
    formattedBatteryLimit,
  } = useReferenceLineConfig({
    wallboxLimit,
    batteryLimit,
    selectedDynamicPowerPriceModalSetting,
    minPrice,
    maxPrice,
    isModal,
    isDynamicPowerPriceEnabledForBattery,
    isDynamicPowerPriceEnabledForWallboxes,
  })

  const showDynamicEmsData = isModal ? selectedDynamicPowerPriceModalSetting === DynamicPriceType.Battery : isDynamicEmsEnabled

  const handleOnClick = (): void => {
    if (isModal) setShowStaticDot(false)
  }

  const handleMouseLeave = (): void => {
    if (isModal) setShowStaticDot(true)
  }

  //* Map the chart data to include the battery mode */
  const mappedChartData = chartData.map(item => {
    const emsItem = dynamicEmsData?.find(emsData => {
      const from = moment(item.from)
      const to = moment(item.to)
      const emsStart = moment(emsData.start)
      const emsEnd = moment(emsData.end)

      return from.isSameOrAfter(emsStart) && to.isSameOrBefore(emsEnd)
    })

    if (emsItem) {
      return { ...item, batteryMode: emsItem.emsBatteryMode }
    }

    return { ...item, batteryMode: EmsBatteryMode.NormalOperation }
  })

  const lastChartDataIndex = chartData.length - 1

  const getChargingType = (batteryType: EmsBatteryMode, value: number | null): { [key: string]: number | null } => {
    if (batteryType === EmsBatteryMode.GridCharge) {
      return { priceGrid: value }
    } else if (batteryType === EmsBatteryMode.BlockDischarging) {
      return { priceDischargeStop: value }
    } else if (batteryType === EmsBatteryMode.NormalOperation) {
      return { priceNormal: value }
    }
    return { priceNormal: value }
  }

  const convertedMappedData = mappedChartData.map((item, index) => {
    const { batteryMode, price } = item
    const chargingType = getChargingType(batteryMode, price)

    if (index !== lastChartDataIndex && index !== 0 && mappedChartData[index - 1].batteryMode !== batteryMode) {
      return { ...item, ...chargingType, ...getChargingType(mappedChartData[index - 1].batteryMode, 0) }
    }

    return { ...item, ...chargingType }
  })
  const data = isDynamicEmsEnabled ? convertedMappedData : chartData

  const renderActiveDot = (d: AreaDot, color?: string): JSX.Element => {
    if (showStaticDot || d?.payload?.price === null) return <></>
    return <HoverDot data={d} color={color || chartColorConfig.stroke} />
  }

  const renderStaticDot = (d: AreaDot, color?: string): JSX.Element => {
    if (!showStaticDot || d?.payload?.price === null) return <></>
    return <CustomizedDot key={d.index} data={d} currentPowerPriceValues={currentPowerPriceValues} color={color || chartColorConfig.stroke} />
  }

  const yAxis = YAxisPowerPriceChart({ isModal: !!isModal, yAxisDomain })
  const xAxis = XAxisPowerPriceChart({ isModal: !!isModal, lastItemIndex: lastChartDataIndex })

  /* for ai360v2 user, show only the power price in line chart */
  if (isDynamicEmsV2Accessible) {
    return (
      <PowerPriceChartAI360
        chartData={chartData}
        classChartContainer={classes.chartContainer}
        isModal={!!isModal}
        handleOnClick={handleOnClick}
        handleMouseLeave={handleMouseLeave}
        getTooltipValues={getTooltipValues}
        renderActiveDot={renderActiveDot}
        renderStaticDot={renderStaticDot}
        yAxisDomain={yAxisDomain}
        minChartHeight={minChartHeight}
      />
    )
  }

  return (
    <ResponsiveContainer minHeight={minChartHeight} height="100%" className={classes.chartContainer}>
      <AreaChart
        data={data}
        style={{ position: 'relative', cursor: 'inherit' }}
        margin={{
          top: isModal ? 30 : 3,
          right: 0,
          left: 0,
          bottom: 0,
        }}
        onClick={handleOnClick}
        onMouseEnter={handleOnClick}
        // recharts calls onMouseDown on touchstart anywhere in the chart, so a drag without a prior tap also enables the hover dot
        onMouseDown={handleOnClick}
        onMouseLeave={handleMouseLeave}
      >
        {isModal && <CartesianGrid vertical={false} />}
        {isDynamicEmsEnabled && showDynamicEmsData && (
          <>
            <defs>
              <linearGradient id="normal" x1="0" y="0" x2="0" y2="1">
                <stop offset="5%" stopColor={theme.palette.dppWidget.dynamicEmsNormalOperationColor} />
                <stop offset="95%" stopColor={theme.palette.dppWidget.dynamicEmsNormalOperationColor} />
              </linearGradient>
              <linearGradient id="grid" x1="0" y="0" x2="0" y2="1">
                <stop offset="5%" stopColor={theme.palette.dppWidget.dynamicEmsGridChargeColor} />
                <stop offset="95%" stopColor={theme.palette.dppWidget.dynamicEmsGridChargeColor} />
              </linearGradient>
              <linearGradient id="discharge-stop" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={theme.palette.dppWidget.dynamicEmsBlockDischargingColor} />
                <stop offset="95%" stopColor={theme.palette.dppWidget.dynamicEmsBlockDischargingColor} />
              </linearGradient>
            </defs>
            {!isDynamicEmsDataLoading && (
              <>
                <Area
                  type="stepAfter"
                  dataKey="priceGrid"
                  stroke={'transparent'}
                  fill={'url(#grid)'}
                  activeDot={false}
                  isAnimationActive={false}
                  onClick={handleOnClick}
                  onMouseEnter={handleOnClick}
                  onTouchStart={handleOnClick}
                  onTouchEnd={handleMouseLeave}
                />
                <Area
                  type="stepAfter"
                  dataKey="priceNormal"
                  stroke={'transparent'}
                  fill={'url(#normal)'}
                  activeDot={false}
                  isAnimationActive={false}
                  onClick={handleOnClick}
                  onMouseEnter={handleOnClick}
                  onTouchStart={handleOnClick}
                  onTouchEnd={handleMouseLeave}
                />
                <Area
                  type="stepAfter"
                  dataKey="priceDischargeStop"
                  stroke={'transparent'}
                  fill={'url(#discharge-stop)'}
                  activeDot={false}
                  isAnimationActive={false}
                  onClick={handleOnClick}
                  onMouseEnter={handleOnClick}
                  onTouchStart={handleOnClick}
                  onTouchEnd={handleMouseLeave}
                />
              </>
            )}
            {isDynamicEmsDataLoading && (
              <Area
                type="stepAfter"
                dataKey="price"
                stroke={chartColorConfig.stroke}
                fill={theme.palette.grey.grey06}
                isAnimationActive={false}
                onClick={handleOnClick}
                onMouseEnter={handleOnClick}
                onTouchStart={handleOnClick}
                onTouchEnd={handleMouseLeave}
              />
            )}
            <Area
              type="stepAfter"
              dataKey="price"
              stroke={theme.palette.dppWidget.consumptionPriceColor}
              fill={'transparent'}
              isAnimationActive={false}
              activeDot={renderActiveDot}
              dot={renderStaticDot}
              onClick={handleOnClick}
              onMouseEnter={handleOnClick}
              onTouchStart={handleOnClick}
              onTouchEnd={handleMouseLeave}
            />
          </>
        )}
        {(!isDynamicEmsEnabled || !showDynamicEmsData) && (
          <Area
            type="stepAfter"
            dataKey="price"
            stroke={chartColorConfig.stroke}
            fill={chartColorConfig.fill}
            activeDot={renderActiveDot}
            dot={renderStaticDot}
            isAnimationActive={false}
            onClick={handleOnClick}
            onMouseEnter={handleOnClick}
            onTouchStart={handleOnClick}
            onTouchEnd={handleMouseLeave}
          />
        )}
        {yAxis}
        {xAxis}

        {isModal && <Tooltip cursor={false} content={getTooltipValues} />}

        {hasNegativePrices && <ReferenceLine y={0} stroke={theme.palette.grey.grey04} strokeWidth={2} />}
        {showReferenceLineForBattery && !isDynamicEmsEnabled && (
          <ReferenceLine
            y={batteryReferenceLineYPosition}
            stroke={dynamicPriceColorConfig[DynamicPriceType.Battery]}
            strokeWidth={2}
            strokeDasharray="5 5"
          >
            {isModal && (
              <Label
                style={{ fill: dynamicPriceColorConfig[DynamicPriceType.Battery], marginBottom: theme.spacing(1) }}
                value={formattedBatteryLimit}
                position={'insideBottomRight'}
              />
            )}
          </ReferenceLine>
        )}
        {showReferenceLineForWallbox && (
          <ReferenceLine
            y={wallboxReferenceLineYPosition}
            stroke={dynamicPriceColorConfig[DynamicPriceType.Wallbox]}
            strokeWidth={2}
            strokeDasharray="5 5"
          >
            {isModal && (
              <Label
                style={{ fill: dynamicPriceColorConfig[DynamicPriceType.Wallbox], marginBottom: theme.spacing(1) }}
                value={formattedWallboxLimit}
                position={'insideBottomRight'}
              />
            )}
          </ReferenceLine>
        )}
      </AreaChart>
    </ResponsiveContainer>
  )
}

export default PowerPriceChart
